import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Linkedin, Github,Twitter, Users, Crown, Lightbulb, MapPin, GraduationCap, Award } from 'lucide-react';
import manav from "../assets/manav.jpg";
import pavan from "../assets/pavan.jpg";
import logo from "../assets/logo.jpg";
import './Founders.css';
import SEOHead from "./SEOHead";

const Founders = () => {
  const [activeFounder, setActiveFounder] = useState(0);
    const navigate = useNavigate();

  const founders = [
    {
      id: 1,
      name: "Pavan",
      role: "Visionary Founder ",
      location: "USA",
      background: "Cybersecurity Expert & Tech Entrepreneur",
      education: "BSc Cybersecurity, University of East London (UEL), UK",
      image: pavan,
      story: {
        title: "The Visionary Leader",
        description: "Pavan's journey as an international student studying Cybersecurity at UEL gave him firsthand experience of the challenges faced by students abroad. His technical background combined with real-world struggles sparked the vision for ComLinkr.",
        keyMoments: [
          "Experienced the challenges of finding suitable accommodation as an international student in the UK",
          "Recognized the gap between existing platforms and real student needs",
          "Combined cybersecurity expertise with entrepreneurial vision",
          "Co-founded ComLinkr to solve problems he personally experienced"
        ]
      },
      expertise: ["Cybersecurity", "Business Strategy", "Product Vision", "Digital Innovation"],
      philosophy: "Innovation doesn't start with technology—it starts with seeing what others overlook and daring to build what others haven't.",
      personalSide: {
        hobbies: ["Technology Innovation", "Strategic Planning", "Cultural Exploration", "Mentoring"],
        funFact: "Transformed a simple conversation into a growing communication startup",
        favoriteQuote: "Leading with vision, building with purpose."
      },
      achievements: [
        "Founded ComLinkr from a personal need into a scalable solution",
        "Successfully bridged technical expertise with business acumen",
        "Built a platform addressing real international student challenges",
        "Leading business strategy and long-term vision for ComLinkr"
      ],
      vision: "I see ComLinkr as more than a platform—it's a bridge that connects people to communities where they truly belong, solving the fundamental challenge of finding home in a foreign place.",
      social: {
        linkedin: "#",
        github: "#",
        twitter: "#"
      }
    },
    {
      id: 2,
      name: "Manav",
      role: "Founder",
      location: "London, UK",
      background: "Computer Science Graduate & Tech Innovator",
      education: "BSc (Hons) Computer Science, London",
      image: manav,
      story: {
        title: "The Technical Architect",
        description: "Manav's strong foundation in Computer Science from London, combined with his bold vision for the future, drives the technological innovation at ComLinkr. His commitment to creating meaningful digital experiences shapes the platform's development.",
        keyMoments: [
          "Graduated with honors in Computer Science from London",
          "Shared the vision during a candid conversation about student challenges abroad",
          "Recognized the market gap and joined forces to co-found ComLinkr",
          "Leading the technical development and digital innovation"
        ]
      },
      expertise: ["Computer Science", "Software Development", "Digital Innovation", "User Experience"],
      philosophy: "Technology should create meaningful digital experiences that connect people, solve real problems, and shape what's next.",
      personalSide: {
        hobbies: ["Software Development", "Digital Design", "Problem Solving", "Tech Innovation"],
        funFact: "Turned a simple conversation into a comprehensive platform architecture",
        favoriteQuote: "Building meaningful connections through technology"
      },
      achievements: [
        "Co-founded ComLinkr from concept to reality",
        "Architected the technical foundation of the platform",
        "Created digital experiences that solve real-world problems",
        "Leading technical innovation and product development"
      ],
      vision: "ComLinkr will revolutionize how international students and expats find not just accommodation, but genuine community and belonging in new places.",
      social: {
        linkedin: "#",
        github: "#",
        twitter: "#"
      }
    }
  ];

  const founderStory = {
    meetingStory: "The idea for ComLinkr was sparked during a candid conversation between two friends, both of whom had firsthand experience living abroad as international students. While discussing the common challenges faced by students and individuals in a foreign country—especially when it comes to finding suitable accommodation, navigating cultural differences, and building a sense of community—one of them mentioned the concept of a platform tailored to these exact needs.",
    foundingMoment: "The idea immediately resonated. What started as a simple discussion quickly turned into a shared vision. Recognizing the immense gap in the market and the real struggles faced by countless people like them, the two friends decided to join forces. With a clear purpose in mind and a commitment to solving real-world problems, they co-founded ComLinkr.",
    sharedValues: [
      "Solving real problems faced by everyone whether an international student or a local resident or anyone",
      "Creating genuine community connections, not just transactions",
      "Building technology that bridges cultural and geographical gaps",
      "Making everyone feel at home, wherever they are in the world"
    ]
  };

  const timeline = [
    { year: "Early Days", event: "Pavan and Manav experience challenges as international students" },
    { year: "The Spark", event: "Candid conversation reveals shared vision for student-focused platform" },
    { year: "Foundation", event: "ComLinkr co-founded with commitment to solving real-world problems" },
    { year: "Development", event: "Platform architecture designed with student needs at the center" },
    { year: "Growth", event: "ComLinkr evolves beyond housing to community-driven initiative" },
    { year: "Present", event: "Helping international students and expats feel at home worldwide" },
    { year: "Future", event: "Expanding global reach to serve more students and communities" }
  ];

  const FloatingElement = ({ children, delay = 0, className = "" }) => (
    <div 
      className={`floating-element ${className}`}
      style={{
        animationDelay: `${delay}s`,
        animationDuration: '3s'
      }}
    >
      {children}
    </div>
  );

  return (
    <div className="founders-page">
      <SEOHead
        title="Our Founders — ComLinkr | Pavan Rao & Manav Trivedi"
        description="Meet Pavan Rao and Manav Trivedi, the founders of ComLinkr — building the community-first platform for housing, food, and belonging."
        canonical="https://www.comlinkr.com/founders"
      />
      {/* Header Section */}
      <section className="founders-header">
        {/* Floating Background Elements */}
        <FloatingElement className="float-1" delay={0}>👑</FloatingElement>
        <FloatingElement className="float-2" delay={1}>🚀</FloatingElement>
        <FloatingElement className="float-3" delay={2}>💡</FloatingElement>
        <FloatingElement className="float-4" delay={3}>🌟</FloatingElement>
        <FloatingElement className="float-5" delay={4}>🎯</FloatingElement>
        <FloatingElement className="float-6" delay={5}>✨</FloatingElement>

        <div className="header-content">
          <div className="founders-logo">
            <div className="logo-inner">
              <img src={logo} alt="ComLinkr Logo" className="logo-image" />
            </div>
          </div>
          
          <h1 className="founders-title">
            Meet Our <span className="gradient-text">Founders</span>
          </h1>
          
          <p className="founders-subtitle">
            The visionary minds who turned a simple conversation into a revolutionary platform
          </p>
        </div>
      </section>

      {/* Founders Showcase */}
      <section className="founders-showcase">
        <div className="container">
          {/* Toggle Buttons */}
          <div className="founders-toggle">
            {founders.map((founder, index) => (
              <button
                key={founder.id}
                onClick={() => setActiveFounder(index)}
                className={`founder-toggle-btn ${activeFounder === index ? 'active' : ''}`}
              >
                <img 
                  src={founder.image} 
                  alt={founder.name}
                  className="toggle-btn-image"
                />
                <span className="toggle-btn-text">
                  {founder.name}
                </span>
              </button>
            ))}
          </div>

          {/* Active Founder Detail */}
          <div className="founder-detail-card">
            <div className="founder-detail-grid">
              <div className="founder-image-section">
                <div className="founder-image-wrapper">
                  <img 
                    src={founders[activeFounder].image} 
                    alt={founders[activeFounder].name}
                    className="founder-main-image"
                  />
                  <div className="crown-badge">
                    <Crown size={24} />
                  </div>
                </div>
              </div>
              
              <div className="founder-info-section">
                <h2 className="founder-name">
                  {founders[activeFounder].name}
                </h2>
                <p className="founder-role">{founders[activeFounder].role}</p>
                <p className="founder-location">
                  <MapPin size={20} className="location-icon" />
                  {founders[activeFounder].location}
                </p>
                <p className="founder-background">{founders[activeFounder].background}</p>
                <p className="founder-education">
                  <GraduationCap size={20} />
                  {founders[activeFounder].education}
                </p>
                
                <div className="philosophy-box">
                  <h5 className="philosophy-title">Philosophy</h5>
                  <p className="philosophy-text">
                    "{founders[activeFounder].philosophy}"
                  </p>
                </div>

                <div className="social-links">
                  {Object.entries(founders[activeFounder].social).map(([platform, url]) => {
                    const IconComponent = platform === 'linkedin' ? Linkedin : platform === 'github' ? Github : Twitter;
                    
                    return (
                      <a 
                        key={platform} 
                        href={url} 
                        className={`social-link social-${platform}`}
                      >
                        <IconComponent size={20} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Story Deep Dive */}
      <section className="founder-story-section">
        <div className="container">
          <div className="story-grid">
            <div className="story-card">
              <div className="story-header">
                <h3>{founders[activeFounder].story.title}</h3>
              </div>
              <div className="story-content">
                <p className="story-description">{founders[activeFounder].story.description}</p>
                
                <h5 className="journey-title">Key Journey Moments</h5>
                <ul className="journey-moments">
                  {founders[activeFounder].story.keyMoments.map((moment, index) => (
                    <li key={index} className="journey-item">
                      {moment}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="details-card">
              <div className="expertise-section">
                <h5 className="expertise-title">Core Expertise</h5>
                <div className="expertise-tags">
                  {founders[activeFounder].expertise.map((skill, index) => (
                    <span key={index} className="expertise-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="personal-section">
                <h5 className="personal-title">Personal Side</h5>
                <div className="personal-content">
                  <div className="personal-item">
                    <strong>Hobbies:</strong> {founders[activeFounder].personalSide.hobbies.join(", ")}
                  </div>
                  <div className="personal-item">
                    <strong>Fun Fact:</strong> {founders[activeFounder].personalSide.funFact}
                  </div>
                  <div className="personal-quote">
                    <strong>Favorite Quote:</strong> "{founders[activeFounder].personalSide.favoriteQuote}"
                  </div>
                </div>
              </div>

              <div className="achievements-section">
                <h5 className="achievements-title">Key Achievements</h5>
                <ul className="achievements-list">
                  {founders[activeFounder].achievements.map((achievement, index) => (
                    <li key={index} className="achievement-item">
                      <Award size={16} className="achievement-icon" />
                      {achievement}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          
          <div className="vision-card">
            <h4 className="vision-title">Vision for ComLinkr</h4>
            <p className="vision-text">
              "{founders[activeFounder].vision}"
            </p>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="origin-story-section">
        <div className="container">
          <h2 className="section-title">How ComLinkr Was Born</h2>
          
          <div className="story-timeline">
            <div className="story-step">
              <h4 className="step-title">
                <Lightbulb className="step-icon" />
                The Spark
              </h4>
              <p className="step-content">{founderStory.meetingStory}</p>
            </div>
            
            <div className="story-step">
              <h4 className="step-title">
                <Users className="step-icon" />
                The Decision
              </h4>
              <p className="step-content">{founderStory.foundingMoment}</p>
            </div>
            
            <div className="story-step">
              <h4 className="step-title">
                🚀 The Growth
              </h4>
              <p className="step-content">
                Since then, ComLinkr has grown into more than just a housing platform—it's a community-driven initiative that helps international students and expats feel at home, wherever they are.
              </p>
            </div>
          </div>

          <div className="shared-values-section">
            <h4 className="values-title">Our Shared Values</h4>
            <div className="values-grid">
              {founderStory.sharedValues.map((value, index) => (
                <div key={index} className="value-item">
                  <span className="value-icon">✨</span>
                  <p className="value-text">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="timeline-section">
        <div className="container">
          <h2 className="section-title">Our Journey</h2>
          
          <div className="timeline-wrapper">
            <div className="timeline-line"></div>
            
            {timeline.map((item, index) => (
              <div key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
                <div className="timeline-content">
                  <div className="timeline-dot"></div>
                  <div className="timeline-arrow"></div>
                  
                  <h5 className="timeline-year">{item.year}</h5>
                  <p className="timeline-event">{item.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Navigation Section */}
      <section className="team-navigation">
        <div className="container text-center">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3>Want to Learn More?</h3>
            <div className="navigation-buttons">
              <motion.button 
                className="btn btn-primary btn-nav"
                onClick={() => navigate('/team')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>👑</span> Meet Our Team
              </motion.button>
              <motion.button 
                className="btn btn-outline-primary btn-nav"
                onClick={() => navigate('/')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>🏠</span> Back to Home
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Founders;